import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Modal, Button } from 'react-bootstrap';
import {
  Sparkles,
  Copy,
  Download,
  Trash2,
  Loader2,
  AlertCircle,
  RefreshCw,
  Info,
  MessageSquare,
  Eye,
  ShieldCheck,
} from 'lucide-react';
import {
  api,
  TAG_PERSONA_SNAPSHOTS,
  useFetchPersonaSnapshotsQuery,
  useFetchPersonaSnapshotQuery,
  useLazyFetchPersonaSnapshotQuery,
  useDeletePersonaSnapshotMutation,
} from '#store';
import { Routes } from '#Routes';
import { GeneratePersonaModal } from '#components/persona/GeneratePersonaModal';

/** 401/403 from RTK Query / fetchBaseQuery — expired session or missing permissions. */
function isUnauthorizedApiError(error) {
  if (!error || typeof error !== 'object') return false;
  const status = error.status ?? error.originalStatus ?? error.data?.status_code;
  return status === 401 || status === 403;
}

function isInProgressStatus(status) {
  return String(status ?? '').toUpperCase() === 'IN_PROGRESS';
}

const MOCK_PERSONA_MARKDOWN_PL = `## Profil kliniczny (skrót)

- **Wiek / płeć:** dane z wywiadu zdrowotnego  
- **Chroniczne:** nadciśnienie tętnicze (kontrolowane), alergia sezonowa  
- **Leki:** zgodnie z ostatnim dokumentem medycznym (nie modyfikuj bez konsultacji)

### Ostatnie badania laboratoryjne
- Morfologia, lipidogram, glukoza z 2025 — wartości w normie referencyjnej laboratorium.

### Styl komunikacji
Preferuj jasny, zwięzły język; unikaj diagnoz zastępczych — sugeruj konsultację ze specjalistą.

---
*Ten tekst jest przykładowy, gdy jeszcze nie masz wygenerowanej treści persony.*`;

const MOCK_PERSONA_MARKDOWN_EN = `## Clinical profile (summary)

- **Age / sex:** from health interview  
- **Chronic:** controlled hypertension, seasonal allergy  
- **Meds:** per latest medical document (do not change without a clinician)

### Recent labs
- CBC, lipid panel, glucose (2025) — within lab reference ranges.

### Communication style
Prefer clear, concise language; avoid substitute diagnoses — suggest specialist follow-up.

---
*This text is sample content when you have not yet generated your persona.*`;

function formatSnapshotDate(iso, locale) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(locale === 'pl' ? 'pl-PL' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(d);
  } catch {
    return null;
  }
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

function buildSummaryLine(snapshot, t) {
  if (snapshot.summary?.trim()) return snapshot.summary.trim();
  const docs = snapshot.documentCount;
  const labs = snapshot.labCount;
  if (Number.isFinite(docs) || Number.isFinite(labs)) {
    return t('medical-persona.summary-from-counts', {
      docs: Number.isFinite(docs) ? docs : '—',
      labs: Number.isFinite(labs) ? labs : '—',
    });
  }
  return t('medical-persona.summary-fallback');
}

export default function MedicalPersonaPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('pl') ? 'pl' : 'en';
  const location = useLocation();
  const dispatch = useDispatch();
  const ownerId = useSelector((state) => state.patient?.id);

  const [generationStarted, setGenerationStarted] = useState(false);
  const [genModalOpen, setGenModalOpen] = useState(false);
  const prevProcessingRef = useRef(false);

  const {
    data: snapshots = [],
    isLoading: listLoading,
    isError: listError,
    error: listQueryError,
    refetch,
    isFetching: listFetching,
  } = useFetchPersonaSnapshotsQuery();

  const sortedSnapshots = useMemo(() => {
    return [...snapshots].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
  }, [snapshots]);

  const newestSnapshot = sortedSnapshots[0] ?? null;
  const isProcessingSnapshot = isInProgressStatus(newestSnapshot?.processStatus);
  const shouldPollGenerations = generationStarted || isProcessingSnapshot;

  useEffect(() => {
    if (!shouldPollGenerations) return undefined;
    const id = window.setInterval(() => {
      refetch();
    }, 3000);
    return () => window.clearInterval(id);
  }, [shouldPollGenerations, refetch]);

  useEffect(() => {
    const wasProcessing = prevProcessingRef.current;
    if (wasProcessing && !isProcessingSnapshot) {
      setGenerationStarted(false);
      /* Polling only refetches the list; GET snapshot/{uuid} stays cached empty from IN_PROGRESS.
         Invalidate all PersonaSnapshots (not only currentUuid — hero may follow isActive, job is newest row). */
      dispatch(api.util.invalidateTags([{ type: TAG_PERSONA_SNAPSHOTS }]));
    }
    prevProcessingRef.current = isProcessingSnapshot;
  }, [isProcessingSnapshot, dispatch]);

  const currentSnapshot = useMemo(() => {
    const active = sortedSnapshots.find((s) => s.isActive);
    if (active) return active;
    return sortedSnapshots[0] ?? null;
  }, [sortedSnapshots]);

  const currentUuid = currentSnapshot?.uuid;
  const listHasMarkdown = Boolean(currentSnapshot?.markdown?.trim());

  const {
    data: detailSnapshot,
    isFetching: detailFetching,
    isError: detailError,
    error: detailQueryError,
  } = useFetchPersonaSnapshotQuery(currentUuid, {
    skip: !currentUuid || listHasMarkdown,
  });

  const sessionExpired =
    (listError && isUnauthorizedApiError(listQueryError)) ||
    (detailError && isUnauthorizedApiError(detailQueryError));

  const loginHref = useMemo(() => {
    const lang = locale;
    const redirectTo = encodeURIComponent(`${location.pathname}${location.search || ''}`);
    return Routes.Login.withLanguageAndRedirect(lang, redirectTo);
  }, [locale, location.pathname, location.search]);

  const isGeneratingView = !sessionExpired && (generationStarted || isProcessingSnapshot);

  const heroLoading =
    (listLoading || (Boolean(currentUuid) && !listHasMarkdown && detailFetching)) &&
    !isGeneratingView;

  const mockFallback = locale === 'pl' ? MOCK_PERSONA_MARKDOWN_PL : MOCK_PERSONA_MARKDOWN_EN;

  /**
   * API body text (parsed e.g. from `payload.markdown`) — no mock fallback.
   * Prefer list row markdown over cached detail: while IN_PROGRESS we fetch detail (often empty);
   * after completion the list refetch has the body, but detail cache can stay stale and skip refetch
   * when `listHasMarkdown` — old `detailSnapshot` must not shadow fresh `currentSnapshot.markdown`.
   */
  const apiMarkdownTrimmed = useMemo(() => {
    const listMd =
      typeof currentSnapshot?.markdown === 'string' ? currentSnapshot.markdown.trim() : '';
    const detailMd =
      typeof detailSnapshot?.markdown === 'string' ? detailSnapshot.markdown.trim() : '';
    return listMd || detailMd;
  }, [detailSnapshot, currentSnapshot]);

  const heroMarkdown = useMemo(() => {
    if (sessionExpired) return '';
    return apiMarkdownTrimmed || mockFallback;
  }, [apiMarkdownTrimmed, mockFallback, sessionExpired]);

  /** Show “sample” label only when there is no generated API content yet (not while detail is loading). */
  const isMockPreview =
    !sessionExpired && !heroLoading && !isGeneratingView && apiMarkdownTrimmed.length === 0;

  const generatedLabel = useMemo(() => {
    const iso = detailSnapshot?.createdAt ?? currentSnapshot?.createdAt;
    const formatted = formatSnapshotDate(iso, locale);
    if (formatted) return t('medical-persona.generated-at', { date: formatted });
    return t('medical-persona.generated-unknown');
  }, [detailSnapshot, currentSnapshot, locale, t]);

  const [fetchSnapshot] = useLazyFetchPersonaSnapshotQuery();
  const [deleteSnapshot, { isLoading: isDeleting }] = useDeletePersonaSnapshotMutation();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMarkdown, setPreviewMarkdown] = useState('');

  const resolveSnapshotMarkdown = useCallback(
    async (snap) => {
      if (snap.markdown?.trim()) return snap.markdown;
      const res = await fetchSnapshot(snap.uuid).unwrap();
      return res?.markdown?.trim() ?? '';
    },
    [fetchSnapshot],
  );

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewMarkdown('');
    setPreviewLoading(false);
  }, []);

  const handleOpenPreview = useCallback(
    async (snap) => {
      setPreviewOpen(true);
      setPreviewMarkdown('');
      setPreviewLoading(true);
      try {
        const text = await resolveSnapshotMarkdown(snap);
        setPreviewMarkdown(text);
      } catch {
        toast.error(t('medical-persona.preview-modal-load-error'));
        setPreviewMarkdown('');
      } finally {
        setPreviewLoading(false);
      }
    },
    [resolveSnapshotMarkdown, t],
  );

  const handleCopyHero = useCallback(async () => {
    try {
      await copyToClipboard(heroMarkdown);
      toast.success(t('medical-persona.toast-copied'));
    } catch {
      toast.error(t('medical-persona.toast-copy-failed'));
    }
  }, [heroMarkdown, t]);

  const handleDownloadHero = useCallback(() => {
    const name = currentUuid ? `persona-ai-${currentUuid}.txt` : 'persona-ai.txt';
    downloadTextFile(name, heroMarkdown);
    toast.success(t('medical-persona.toast-downloaded'));
  }, [currentUuid, heroMarkdown, t]);

  const handleGenerationStarted = useCallback(() => {
    toast.success(t('medical-persona.toast-generate-started'));
    setGenerationStarted(true);
    refetch();
  }, [refetch, t]);

  const handleDelete = useCallback(
    async (snap) => {
      if (!window.confirm(t('medical-persona.delete-confirm'))) return;
      try {
        await deleteSnapshot(snap.uuid).unwrap();
        toast.success(t('medical-persona.toast-deleted'));
      } catch (err) {
        toast.error(err?.data?.detail ?? t('medical-persona.toast-delete-error'));
      }
    },
    [deleteSnapshot, t],
  );

  const handleRowCopy = useCallback(
    async (snap) => {
      try {
        const text = await resolveSnapshotMarkdown(snap);
        if (!text) {
          toast.error(t('medical-persona.toast-no-content'));
          return;
        }
        await copyToClipboard(text);
        toast.success(t('medical-persona.toast-copied'));
      } catch {
        toast.error(t('medical-persona.toast-copy-failed'));
      }
    },
    [resolveSnapshotMarkdown, t],
  );

  const handleRowDownload = useCallback(
    async (snap) => {
      try {
        const text = await resolveSnapshotMarkdown(snap);
        if (!text) {
          toast.error(t('medical-persona.toast-no-content'));
          return;
        }
        downloadTextFile(`persona-ai-${snap.uuid}.txt`, text);
        toast.success(t('medical-persona.toast-downloaded'));
      } catch {
        toast.error(t('medical-persona.toast-download-failed'));
      }
    },
    [resolveSnapshotMarkdown, t],
  );

  return (
    <>
      <Helmet>
        <title>{t('title.medical-persona-ai')}</title>
      </Helmet>

      <div className='medical-persona-page p-4 bg-light min-vh-100 pb-5'>
        <div className='medical-persona-page__inner'>
          <section className='medical-persona-hero'>
            <div className='medical-persona-hero__head'>
              <div>
                <h1 className='medical-persona-hero__title'>{t('medical-persona.hero-title')}</h1>
                <p className='medical-persona-hero__subtitle'>
                  {t('medical-persona.hero-subtitle')}
                </p>
              </div>
              <div className='medical-persona-hero__meta'>
                <span className='medical-persona-badge--current'>
                  {t('medical-persona.badge-current')}
                </span>
                <span className='medical-persona-hero__date'>{generatedLabel}</span>
              </div>
            </div>

            <div className='medical-persona-howto'>
              <h3 className='medical-persona-howto__heading'>
                <Info className='medical-persona-icon' strokeWidth={2} aria-hidden />
                {t('medical-persona.howto-heading')}
              </h3>
              <div className='medical-persona-howto__grid'>
                <div className='medical-persona-howto__step'>
                  <div className='medical-persona-howto__step-icon' aria-hidden>
                    <Copy strokeWidth={2} />
                  </div>
                  <p className='medical-persona-howto__step-title'>
                    {t('medical-persona.howto-step1-title')}
                  </p>
                  <p className='medical-persona-howto__step-desc'>
                    {t('medical-persona.howto-step1-desc')}
                  </p>
                </div>
                <div className='medical-persona-howto__step'>
                  <div className='medical-persona-howto__step-icon' aria-hidden>
                    <MessageSquare strokeWidth={2} />
                  </div>
                  <p className='medical-persona-howto__step-title'>
                    {t('medical-persona.howto-step2-title')}
                  </p>
                  <p className='medical-persona-howto__step-desc'>
                    {t('medical-persona.howto-step2-desc')}
                  </p>
                </div>
                <div className='medical-persona-howto__step'>
                  <div className='medical-persona-howto__step-icon' aria-hidden>
                    <Sparkles strokeWidth={2} />
                  </div>
                  <p className='medical-persona-howto__step-title'>
                    {t('medical-persona.howto-step3-title')}
                  </p>
                  <p className='medical-persona-howto__step-desc'>
                    {t('medical-persona.howto-step3-desc')}
                  </p>
                </div>
              </div>
            </div>

            {sessionExpired ? (
              <div className='medical-persona-alert medical-persona-alert--session' role='alert'>
                <AlertCircle className='medical-persona-icon' aria-hidden />
                <div className='medical-persona-alert__body medical-persona-alert__body--stack'>
                  <span>{t('medical-persona.session-expired-body')}</span>
                  <Link className='medical-persona-alert__login-link' to={loginHref}>
                    {t('medical-persona.session-expired-login')}
                  </Link>
                </div>
              </div>
            ) : null}

            {!sessionExpired && (listError || detailError) ? (
              <div className='medical-persona-alert' role='alert'>
                <AlertCircle className='medical-persona-icon' aria-hidden />
                <div className='medical-persona-alert__body'>
                  <span>{t('medical-persona.load-error')}</span>
                  <button
                    type='button'
                    onClick={() => refetch()}
                    className='medical-persona-btn medical-persona-btn--outline medical-persona-btn--inline'
                  >
                    <RefreshCw className='medical-persona-icon' aria-hidden />
                    {t('medical-persona.retry')}
                  </button>
                </div>
              </div>
            ) : null}

            <div className='medical-persona-hero__actions'>
              <button
                type='button'
                onClick={handleCopyHero}
                disabled={heroLoading || sessionExpired}
                className='medical-persona-btn medical-persona-btn--primary'
              >
                <Sparkles className='medical-persona-icon' strokeWidth={2} aria-hidden />
                {t('medical-persona.btn-copy-prompt')}
              </button>
              <button
                type='button'
                onClick={handleDownloadHero}
                disabled={heroLoading || sessionExpired}
                className='medical-persona-btn medical-persona-btn--outline'
              >
                <Download className='medical-persona-icon' strokeWidth={2} aria-hidden />
                {t('medical-persona.btn-download-txt')}
              </button>
              <span className='medical-persona-hero__actions-spacer' aria-hidden />
              <button
                type='button'
                onClick={() => setGenModalOpen(true)}
                disabled={sessionExpired}
                className='medical-persona-btn medical-persona-btn--muted'
              >
                <Sparkles
                  className='medical-persona-icon medical-persona-icon--teal'
                  strokeWidth={2}
                  aria-hidden
                />
                {t('medical-persona.btn-generate')}
              </button>
            </div>

            <div className='medical-persona-privacy-note' role='note'>
              <ShieldCheck
                className='medical-persona-privacy-note__icon'
                strokeWidth={2}
                aria-hidden
              />
              <p className='medical-persona-privacy-note__text'>
                <span className='medical-persona-privacy-note__lead'>
                  {t('medical-persona.privacy-lead')}
                </span>
                <span className='medical-persona-privacy-note__rest'>
                  {t('medical-persona.privacy-rest')}
                </span>
              </p>
            </div>

            <div className='medical-persona-preview-wrap'>
              {heroLoading ? (
                <div className='medical-persona-preview__loading' aria-busy='true'>
                  <Loader2
                    className='medical-persona-icon medical-persona-icon--lg medical-persona-icon--spin'
                    style={{ color: '#fff' }}
                    aria-hidden
                  />
                </div>
              ) : null}
              {isGeneratingView ? (
                <div
                  className='medical-persona-preview medical-persona-preview--generating'
                  role='status'
                  aria-live='polite'
                  aria-label={t('medical-persona.generating-aria')}
                >
                  <Sparkles
                    className='medical-persona-preview__generating-icon'
                    strokeWidth={2}
                    aria-hidden
                  />
                  <p className='medical-persona-preview__generating-title'>
                    {t('medical-persona.generating-title')}
                  </p>
                  <p className='medical-persona-preview__generating-hint'>
                    {t('medical-persona.generating-hint')}
                  </p>
                </div>
              ) : (
                <div
                  className='medical-persona-preview'
                  role='region'
                  aria-label={t('medical-persona.preview-aria')}
                >
                  {sessionExpired ? (
                    <p className='medical-persona-preview__label'>
                      {t('medical-persona.preview-session-placeholder')}
                    </p>
                  ) : null}
                  {isMockPreview ? (
                    <p className='medical-persona-preview__label'>
                      {t('medical-persona.preview-demo-label')}
                    </p>
                  ) : null}
                  <pre className='medical-persona-preview__pre'>{heroMarkdown}</pre>
                </div>
              )}
            </div>
          </section>

          <section className='medical-persona-history'>
            <h2 className='medical-persona-history__title'>
              {t('medical-persona.history-heading')}
            </h2>
            <p className='medical-persona-history__lead'>{t('medical-persona.history-lead')}</p>

            <div className='medical-persona-history__panel'>
              {listLoading ? (
                <div className='medical-persona-history__loading'>
                  <Loader2
                    className='medical-persona-icon medical-persona-icon--lg medical-persona-icon--spin'
                    aria-hidden
                  />
                </div>
              ) : sessionExpired ? (
                <p className='medical-persona-history__empty'>
                  {t('medical-persona.session-expired-history')}
                </p>
              ) : sortedSnapshots.length === 0 ? (
                <p className='medical-persona-history__empty'>
                  {t('medical-persona.history-empty')}
                </p>
              ) : (
                <div className='medical-persona-table-wrap'>
                  <table className='medical-persona-table'>
                    <thead>
                      <tr>
                        <th scope='col'>{t('medical-persona.col-date')}</th>
                        <th scope='col'>{t('medical-persona.col-summary')}</th>
                        <th scope='col' className='medical-persona-table__actions'>
                          {t('medical-persona.col-actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSnapshots.map((snap) => (
                        <tr key={snap.uuid}>
                          <td className='text-nowrap'>
                            {formatSnapshotDate(snap.createdAt, locale) ?? '—'}
                          </td>
                          <td className='medical-persona-table__summary'>
                            {buildSummaryLine(snap, t)}
                          </td>
                          <td className='medical-persona-table__actions text-nowrap'>
                            <div className='medical-persona-table__icon-btns'>
                              <button
                                type='button'
                                onClick={() => handleOpenPreview(snap)}
                                className='medical-persona-icon-btn'
                                title={t('medical-persona.action-preview')}
                                aria-label={t('medical-persona.action-preview')}
                              >
                                <Eye className='medical-persona-icon' strokeWidth={2} aria-hidden />
                              </button>
                              <button
                                type='button'
                                onClick={() => handleRowCopy(snap)}
                                className='medical-persona-icon-btn'
                                title={t('medical-persona.action-copy')}
                                aria-label={t('medical-persona.action-copy')}
                              >
                                <Copy
                                  className='medical-persona-icon'
                                  strokeWidth={2}
                                  aria-hidden
                                />
                              </button>
                              <button
                                type='button'
                                onClick={() => handleRowDownload(snap)}
                                className='medical-persona-icon-btn'
                                title={t('medical-persona.action-download')}
                                aria-label={t('medical-persona.action-download')}
                              >
                                <Download
                                  className='medical-persona-icon'
                                  strokeWidth={2}
                                  aria-hidden
                                />
                              </button>
                              <button
                                type='button'
                                onClick={() => handleDelete(snap)}
                                disabled={isDeleting}
                                className='medical-persona-icon-btn medical-persona-icon-btn--danger'
                                title={t('medical-persona.action-delete')}
                                aria-label={t('medical-persona.action-delete')}
                              >
                                <Trash2
                                  className='medical-persona-icon'
                                  strokeWidth={2}
                                  aria-hidden
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {listFetching && !listLoading ? (
              <p className='medical-persona-history__refreshing'>
                {t('medical-persona.refreshing')}
              </p>
            ) : null}
          </section>
        </div>
      </div>

      <Modal
        show={previewOpen}
        onHide={handleClosePreview}
        size='lg'
        centered
        scrollable
        className='medical-persona-preview-modal'
        contentClassName='medical-persona-preview-modal__content'
        aria-labelledby='medical-persona-preview-modal-title'
      >
        <Modal.Header closeButton>
          <Modal.Title id='medical-persona-preview-modal-title'>
            {t('medical-persona.preview-modal-title')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='medical-persona-preview-modal__body'>
          {previewLoading ? (
            <div className='medical-persona-preview-modal__loading'>
              <Loader2
                className='medical-persona-icon medical-persona-icon--lg medical-persona-icon--spin'
                aria-hidden
              />
            </div>
          ) : previewMarkdown ? (
            <div className='medical-persona-preview-wrap medical-persona-preview-wrap--modal'>
              <div
                className='medical-persona-preview medical-persona-preview--modal'
                role='region'
                aria-label={t('medical-persona.preview-aria')}
              >
                <pre className='medical-persona-preview__pre'>{previewMarkdown}</pre>
              </div>
            </div>
          ) : (
            <p className='medical-persona-preview-modal__empty'>
              {t('medical-persona.toast-no-content')}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className='medical-persona-preview-modal__footer'>
          <Button variant='outline-secondary' type='button' onClick={handleClosePreview}>
            {t('medical-persona.preview-modal-close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <GeneratePersonaModal
        show={genModalOpen}
        onHide={() => setGenModalOpen(false)}
        ownerId={ownerId}
        onGenerationStarted={handleGenerationStarted}
        defaultLanguage={locale}
      />
    </>
  );
}
